import React, { useEffect, useMemo, useState } from "react";
import { Settings, Plus, Edit, Trash2, X, Zap, Percent, Calendar } from "lucide-react";

/**
 * PayrollConfiguration.jsx
 * - Clean Zoho/Keka-style admin page
 * - Poppins + Roboto fonts
 * - Local mock state (CRUD) + mock payroll test calc
 * - Right-side drawer for approver management and advanced settings
 *
 * Save to: src/pages/PayrollConfiguration.jsx
 */

export default function PayrollConfiguration() {
  // ---------- Mock persisted settings ----------
  const [settings, setSettings] = useState(() => ({
    companyName: "ACME Corp",
    currency: "INR",
    decimalPlaces: 2,
    payrunFrequency: "Monthly", // Monthly / Bi-Weekly / Weekly
    payrollCutoffDay: 25, // day of month when payroll closes
    roundingRule: "Nearest_1", // Nearest_1, Nearest_5, Floor, Ceil
    enableAutoTax: true,
    defaultPayrollApproverId: null,
    published: true,
    lastSavedAt: new Date().toISOString(),
  }));

  // ---------- Approvers (CRUD) ----------
  const [approvers, setApprovers] = useState(() => [
    { id: 1, name: "Ravi Kumar", email: "ravi@acme.com", role: "Finance Manager" },
    { id: 2, name: "Anita Sharma", email: "anita@acme.com", role: "HR Head" },
  ]);

  // UI state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [approverForm, setApproverForm] = useState({ id: null, name: "", email: "", role: "" });
  const [isEditingApprover, setIsEditingApprover] = useState(false);
  const [testPayrollResult, setTestPayrollResult] = useState(null);

  // small helpers
  useEffect(() => {
    // ensure default approver id always valid
    if (settings.defaultPayrollApproverId === null && approvers.length > 0) {
      setSettings((s) => ({ ...s, defaultPayrollApproverId: approvers[0].id }));
    }
  }, [approvers]);

  function openApproverDrawer(edit = null) {
    if (edit) {
      setIsEditingApprover(true);
      setApproverForm({ id: edit.id, name: edit.name, email: edit.email, role: edit.role });
    } else {
      setIsEditingApprover(false);
      setApproverForm({ id: null, name: "", email: "", role: "" });
    }
    setDrawerOpen(true);
  }

  function closeDrawer() {
    setDrawerOpen(false);
    setApproverForm({ id: null, name: "", email: "", role: "" });
    setIsEditingApprover(false);
  }

  function saveApprover(e) {
    e?.preventDefault();
    const { id, name, email, role } = approverForm;
    if (!name.trim() || !email.trim()) {
      alert("Please provide name & email.");
      return;
    }
    if (isEditingApprover) {
      setApprovers((prev) => prev.map((a) => (a.id === id ? { id, name, email, role } : a)));
    } else {
      const newId = Date.now();
      setApprovers((prev) => [{ id: newId, name, email, role }, ...prev]);
      // if no default approver set, use this
      if (!settings.defaultPayrollApproverId) setSettings((s) => ({ ...s, defaultPayrollApproverId: newId }));
    }
    closeDrawer();
  }

  function deleteApprover(id) {
    if (!window.confirm("Delete this approver?")) return;
    setApprovers((prev) => prev.filter((p) => p.id !== id));
    if (settings.defaultPayrollApproverId === id) {
      setSettings((s) => ({ ...s, defaultPayrollApproverId: approvers.length > 1 ? approvers.find((a) => a.id !== id)?.id : null }));
    }
  }

  // update simple setting field
  function setField(field, value) {
    setSettings((prev) => ({ ...prev, [field]: value }));
  }

  // simulate test payroll run (mock calculation)
  function runTestPayroll() {
    // produce a fake breakdown with rounding and tax toggle
    const baseSalary = 50000;
    const allowances = 8000;
    const deductions = 4000;
    const gross = baseSalary + allowances;
    let tax = settings.enableAutoTax ? Math.round(gross * 0.1) : 0; // 10% mock
    let netBeforeRounding = gross - tax - deductions;
    // rounding
    const rounded = applyRounding(netBeforeRounding, settings.roundingRule, settings.decimalPlaces);
    setTestPayrollResult({
      baseSalary,
      allowances,
      deductions,
      tax,
      gross,
      netBeforeRounding,
      rounded,
      currency: settings.currency,
    });
  }

  function applyRounding(value, rule, decimals) {
    const factor = Math.pow(10, decimals);
    if (rule === "Nearest_1") return Math.round(value * factor) / factor;
    if (rule === "Nearest_5") return Math.round(value / 5) * 5;
    if (rule === "Floor") return Math.floor(value * factor) / factor;
    if (rule === "Ceil") return Math.ceil(value * factor) / factor;
    return value;
  }

  // memoized summaries
  const approverCount = useMemo(() => approvers.length, [approvers]);

  // ---------- Render ----------
  return (
    <div className="min-h-screen bg-gray-50 p-6 md:p-8 font-['Poppins','Roboto',sans-serif]">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800 flex items-center gap-3">
              <Settings className="text-teal-600" /> Payroll Configuration
            </h1>
            <p className="text-sm text-gray-500 mt-1">Set global payroll rules, rounding, tax toggle and approvers.</p>
            <div className="text-xs text-gray-400 mt-2">Last saved: {new Date(settings.lastSavedAt).toLocaleString()}</div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => runTestPayroll()}
              className="flex items-center gap-2 text-sm px-3 py-1 rounded-md border bg-white hover:shadow-sm"
              title="Run a mock payroll calculation"
            >
              <Zap size={14} /> Test Payroll
            </button>

            <button
              onClick={() => setField("published", !settings.published)}
              className={`px-3 py-1 rounded-md text-sm ${settings.published ? "bg-green-600 text-white" : "bg-gray-100 text-gray-800"}`}
            >
              {settings.published ? "Published" : "Draft"}
            </button>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-lg shadow p-6 space-y-6">
          {/* Basic settings grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-gray-600">Company</label>
              <input value={settings.companyName} onChange={(e) => setField("companyName", e.target.value)} className="mt-1 w-full border rounded p-2" />
            </div>

            <div>
              <label className="text-sm text-gray-600">Currency</label>
              <select value={settings.currency} onChange={(e) => setField("currency", e.target.value)} className="mt-1 w-full border rounded p-2">
                <option>INR</option>
                <option>USD</option>
                <option>EUR</option>
                <option>GBP</option>
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-600">Decimal Places</label>
              <select value={settings.decimalPlaces} onChange={(e) => setField("decimalPlaces", Number(e.target.value))} className="mt-1 w-full border rounded p-2">
                <option value={0}>0</option>
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
              </select>
            </div>
          </div>

          {/* Payroll frequency & cutoff */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-gray-600">Payrun Frequency</label>
              <select value={settings.payrunFrequency} onChange={(e) => setField("payrunFrequency", e.target.value)} className="mt-1 w-full border rounded p-2">
                <option>Monthly</option>
                <option>Bi-Weekly</option>
                <option>Weekly</option>
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-600">Payroll Cutoff Day</label>
              <input type="number" min={1} max={28} value={settings.payrollCutoffDay} onChange={(e) => setField("payrollCutoffDay", Number(e.target.value))} className="mt-1 w-full border rounded p-2" />
              <div className="text-xs text-gray-400 mt-1">Day of month when payroll closes (1-28)</div>
            </div>

            <div>
              <label className="text-sm text-gray-600">Rounding Rule</label>
              <select value={settings.roundingRule} onChange={(e) => setField("roundingRule", e.target.value)} className="mt-1 w-full border rounded p-2">
                <option value="Nearest_1">Nearest (1)</option>
                <option value="Nearest_5">Nearest (5)</option>
                <option value="Floor">Floor</option>
                <option value="Ceil">Ceil</option>
              </select>
            </div>
          </div>

          {/* Auto tax & default approver */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <label className="text-sm text-gray-600">Enable Auto Tax Calculation</label>
              <div className="mt-2 flex items-center gap-3">
                <input type="checkbox" checked={settings.enableAutoTax} onChange={(e) => setField("enableAutoTax", e.target.checked)} />
                <div className="text-xs text-gray-500">When enabled, system will auto-calc taxes during payrun (mock calculation).</div>
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-600">Default Payroll Approver</label>
              <select value={settings.defaultPayrollApproverId || ""} onChange={(e) => setField("defaultPayrollApproverId", e.target.value ? Number(e.target.value) : null)} className="mt-1 w-full border rounded p-2">
                <option value="">Select Approver</option>
                {approvers.map((a) => <option key={a.id} value={a.id}>{a.name} — {a.role}</option>)}
              </select>
            </div>

            <div className="flex justify-end">
              <button onClick={() => openApproverDrawer(null)} className="flex items-center gap-2 px-3 py-1 rounded-md bg-teal-600 text-white text-sm">
                <Plus size={14} /> Add Approver
              </button>
            </div>
          </div>

          {/* Approver list */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-700">Payroll Approvers ({approverCount})</h3>
              <div className="text-xs text-gray-500">Manage approvers for payroll runs</div>
            </div>

            <div className="bg-gray-50 border rounded p-3">
              {approvers.length === 0 ? (
                <div className="text-sm text-gray-500">No approvers added.</div>
              ) : (
                <ul className="space-y-2">
                  {approvers.map((a) => (
                    <li key={a.id} className="flex items-center justify-between bg-white p-2 rounded border">
                      <div>
                        <div className="font-medium">{a.name}</div>
                        <div className="text-xs text-gray-500">{a.email} • {a.role}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => openApproverDrawer(a)} className="text-indigo-600"><Edit size={16} /></button>
                        <button onClick={() => deleteApprover(a.id)} className="text-red-600"><Trash2 size={16} /></button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Test payroll result */}
          {testPayrollResult ? (
            <div className="bg-gray-50 border rounded p-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">Test Payroll Summary</div>
                <div className="text-xs text-gray-500">Mock calculation</div>
              </div>

              <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
                <div>Base: <strong>{testPayrollResult.currency} {testPayrollResult.baseSalary.toLocaleString()}</strong></div>
                <div>Allowances: <strong>{testPayrollResult.currency} {testPayrollResult.allowances.toLocaleString()}</strong></div>
                <div>Tax: <strong>{testPayrollResult.currency} {testPayrollResult.tax.toLocaleString()}</strong></div>
                <div>Net (rounded): <strong>{testPayrollResult.currency} {testPayrollResult.rounded.toLocaleString()}</strong></div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-500">Run a test payroll to preview calculations.</div>
          )}

          {/* Save footer */}
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-500">Changes are stored locally (demo). Wire to API for persistence.</div>
            <div className="flex items-center gap-3">
              <button onClick={() => runTestPayroll()} className="px-3 py-1 rounded-md border bg-white">Test Payroll</button>
              <button onClick={() => { setSettings((s) => ({ ...s, lastSavedAt: new Date().toISOString() })); alert("Settings saved (demo)."); }} className="px-4 py-1 rounded-md bg-teal-600 text-white">Save Settings</button>
            </div>
          </div>
        </div>
      </div>

      {/* Drawer for Approver create/edit */}
      {drawerOpen && (
        <Drawer title={isEditingApprover ? "Edit Approver" : "Add Approver"} onClose={closeDrawer}>
          <form onSubmit={saveApprover} className="space-y-4">
            <div>
              <label className="text-sm text-gray-600">Full Name</label>
              <input value={approverForm.name} onChange={(e) => setApproverForm((p) => ({ ...p, name: e.target.value }))} className="mt-1 w-full border rounded p-2" required />
            </div>

            <div>
              <label className="text-sm text-gray-600">Email</label>
              <input type="email" value={approverForm.email} onChange={(e) => setApproverForm((p) => ({ ...p, email: e.target.value }))} className="mt-1 w-full border rounded p-2" required />
            </div>

            <div>
              <label className="text-sm text-gray-600">Role / Designation</label>
              <input value={approverForm.role} onChange={(e) => setApproverForm((p) => ({ ...p, role: e.target.value }))} className="mt-1 w-full border rounded p-2" />
            </div>

            <div className="flex justify-end gap-2">
              <button type="button" onClick={closeDrawer} className="px-3 py-1 rounded-md border">Cancel</button>
              <button type="submit" className="px-4 py-1 rounded-md bg-teal-600 text-white">{isEditingApprover ? "Update" : "Add"}</button>
            </div>
          </form>
        </Drawer>
      )}
    </div>
  );
}

/* Drawer component */
function Drawer({ title, children, onClose }) {
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 bg-black bg-opacity-40 z-40" />
      <aside className="fixed right-0 top-0 h-full w-full md:w-2/5 bg-white z-50 shadow-2xl overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="p-2 rounded hover:bg-gray-100"><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </aside>
    </>
  );
}
